import {
  configureStore,
  ThunkAction,
  Action,
  Middleware,
  combineReducers
} from '@reduxjs/toolkit';
import resourcesReducer from '../features/resources/resourcesSlice';
import authReducer from '../features/auth/authSlice';
import { apiSlice } from './apiSlice';
import featureFlagReducer from '../features/featureFlag/featureFlagSlice';

const storageKey = 'BL_CMS_STATE';

const saveTokenToLocalStorageMiddleware: Middleware =
  ({ getState }) =>
  (next) =>
  (action) => {
    const beforeToken = getState().auth.userToken;
    const result = next(action);
    const afterToken = getState().auth.userToken;
    if (beforeToken?.token !== afterToken?.token) {
      const stringyToken = JSON.stringify({ userToken: afterToken });
      try {
        localStorage.setItem(storageKey, stringyToken);
      } catch (e: any) {
        alert(
          'There was an error saving to your browser. You may need to delete some old resources to free up space. ' +
            e?.message
        );
      }
    }
    return result;
  };

const savedState = JSON.parse(localStorage.getItem(storageKey) || '{}');

const rootReducer = combineReducers({
  [apiSlice.reducerPath]: apiSlice.reducer,
  resources: resourcesReducer,
  auth: authReducer,
  featureFlag: featureFlagReducer
});

export function setupStore(preloadedState?: Partial<RootState>) {
  const store = configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware().concat(
        apiSlice.middleware,
        saveTokenToLocalStorageMiddleware
      ),
    preloadedState: {
      auth: {
        status: 'idle',
        userToken: savedState.userToken
      },
      ...preloadedState
    }
  });
  return store;
}

export const store = setupStore();
export type AppDispatch = AppStore['dispatch'];
export type RootState = ReturnType<typeof rootReducer>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  Action<string>
>;
